import { useEffect, useRef, useCallback } from 'react'
import { animate } from 'motion'
import type { AnimationState } from '../types'

export function useMotionAnimate(
  targetRef: React.RefObject<HTMLDivElement | null>,
  state: AnimationState,
  onComplete: () => void
) {
  const animationRef = useRef<ReturnType<typeof animate> | null>(null)

  const stopAnimation = useCallback(() => {
    if (animationRef.current) {
      animationRef.current.stop()
      animationRef.current = null
    }
  }, [])

  const resetElement = useCallback(() => {
    stopAnimation()
    if (targetRef.current) {
      targetRef.current.style.transform = 'none'
      targetRef.current.style.opacity = '1'
    }
  }, [targetRef, stopAnimation])

  useEffect(() => {
    if (!state.isPlaying || !targetRef.current) return

    stopAnimation()

    const { transform, transition } = state
    const el = targetRef.current

    // Build the target keyframes
    const keyframes: Record<string, any> = {
      x: transform.x,
      y: transform.y,
      scale: transform.scale,
      rotate: transform.rotate,
      skewX: transform.skewX,
      skewY: transform.skewY,
      opacity: transform.opacity,
    }

    // Build transition options based on type
    let transitionOptions: Record<string, any> = {}

    switch (transition.type) {
      case 'spring':
        transitionOptions = {
          type: 'spring',
          stiffness: transition.spring.stiffness,
          damping: transition.spring.damping,
          mass: transition.spring.mass,
          bounce: transition.spring.bounce,
        }
        break
      case 'tween':
        transitionOptions = {
          duration: transition.tween.duration,
          ease: transition.tween.ease,
        }
        break
      case 'inertia':
        transitionOptions = {
          type: 'inertia',
          velocity: transition.inertia.velocity,
          power: transition.inertia.power,
          min: transition.inertia.min,
          max: transition.inertia.max,
        }
        break
    }

    animationRef.current = animate(el, keyframes, transitionOptions)

    animationRef.current.then(() => {
      onComplete()
    }).catch(() => {
      // Animation was cancelled
    })

    return () => {
      stopAnimation()
    }
  }, [state.isPlaying])

  return { resetElement, stopAnimation, animationRef }
}
